import { useEffect, useLayoutEffect, useState } from 'react'

import { defaultTenantAccountAppearance } from '@authy/lib-common/entities'

import type { AccountAppearance } from '@/api'

const getAccentTextColor = (accent: string) => {
    const channels = accent.match(/[a-f\d]{2}/gi)!.map((channel) => {
        const value = Number.parseInt(channel, 16) / 255

        if (value <= 0.04045) {
            return value / 12.92
        }

        return ((value + 0.055) / 1.055) ** 2.4
    })

    const luminance = channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722

    return luminance > 0.179 ? '#000000' : '#FFFFFF'
}

export const useApplyAccountAppearance = (appearance: AccountAppearance | undefined) => {
    const { darkModeEnabled, schemes } = appearance ?? defaultTenantAccountAppearance
    const [systemDark, setSystemDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches)

    useEffect(() => {
        if (!darkModeEnabled) {
            return
        }

        const query = window.matchMedia('(prefers-color-scheme: dark)')
        const update = () => setSystemDark(query.matches)

        update()
        query.addEventListener('change', update)

        return () => query.removeEventListener('change', update)
    }, [darkModeEnabled])

    const mode = darkModeEnabled && systemDark ? 'dark' : 'light'
    const { accent, pageBackground, surfaceBackground } = schemes[mode].colors

    useLayoutEffect(() => {
        const root = document.documentElement
        const previousMode = root.dataset.accountThemeMode
        const previousColorScheme = root.style.getPropertyValue('color-scheme')
        const variables = {
            '--account-accent': accent,
            '--account-page': pageBackground,
            '--account-surface': surfaceBackground,
            '--account-on-accent': getAccentTextColor(accent),
        }
        const previousVariables = Object.keys(variables).map((name) => [name, root.style.getPropertyValue(name)])

        root.dataset.accountThemeMode = mode
        root.style.setProperty('color-scheme', mode)

        for (const [name, value] of Object.entries(variables)) {
            root.style.setProperty(name, value)
        }

        return () => {
            if (previousMode === undefined) {
                delete root.dataset.accountThemeMode
            } else {
                root.dataset.accountThemeMode = previousMode
            }

            root.style.setProperty('color-scheme', previousColorScheme)

            for (const [name, value] of previousVariables) {
                if (value) {
                    root.style.setProperty(name, value)
                } else {
                    root.style.removeProperty(name)
                }
            }
        }
    }, [accent, mode, pageBackground, surfaceBackground])

    const faviconUrl = appearance?.faviconUrl

    useEffect(() => {
        if (!faviconUrl) {
            return
        }

        const existingLink = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
        const link = existingLink ?? document.createElement('link')
        const previousHref = link.getAttribute('href')
        const previousType = link.getAttribute('type')

        link.rel = 'icon'
        link.href = faviconUrl
        link.removeAttribute('type')

        if (!existingLink) {
            document.head.appendChild(link)
        }

        return () => {
            if (!existingLink) {
                link.remove()
                return
            }

            if (previousHref === null) {
                link.removeAttribute('href')
            } else {
                link.setAttribute('href', previousHref)
            }

            if (previousType !== null) {
                link.setAttribute('type', previousType)
            }
        }
    }, [faviconUrl])
}
